/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useRef, useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices"; // Adjust path as needed
import { useTranslation } from "react-i18next";

interface Props {
	handlePayloadChange: (field: string, value: string | number) => void;
}

const SampleReference = ({ handlePayloadChange }: Props) => {
	const { t } = useTranslation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFileURL, setSelectedFileURL] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState("");

	const handleClick = () => {
		if (!isUploading) {
			fileInputRef.current?.click();
		}
	};

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setUploadError("");
		setIsUploading(true);

		try {
			const response = await MediaBatchPreparationInitServices.uploadFile(file);

			if (response?.status && response?.statusCode === 200) {
				// Replace "sample_reference_file_id" with your actual backend field name
				handlePayloadChange("file_id", Number(response?.file_id));
				const objectUrl = URL.createObjectURL(file);
				setSelectedFileURL(objectUrl);
			} else {
				setUploadError("Failed to upload image. Please try again.");
			}
		} catch (error) {
			setUploadError("File upload failed");
		} finally {
			setIsUploading(false);
		}
	};

	useEffect(() => {
		return () => {
			if (selectedFileURL) URL.revokeObjectURL(selectedFileURL); // Cleanup memory
		};
	}, [selectedFileURL]);

	return (
		<div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm flex flex-col md:flex-row gap-8 items-center">
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleImageUpload}
				accept="image/*"
				className="hidden"
			/>

			<div
				onClick={handleClick}
				className={`w-full md:w-32 h-32 bg-slate-50 rounded-2xl flex items-center justify-center border transition-colors overflow-hidden ${
					isUploading
						? "border-orange-300 border-dashed cursor-not-allowed"
						: "border-slate-200 border-dashed hover:border-orange-500 cursor-pointer group"
				}`}
			>
				{isUploading ? (
					<span className="material-symbols-outlined text-4xl text-orange-400 animate-pulse">
						hourglass_top
					</span>
				) : selectedFileURL ? (
					<img
						src={selectedFileURL}
						alt="Sample Reference"
						className="w-full h-full object-cover"
					/>
				) : (
					<span className="material-symbols-outlined text-4xl text-slate-300 group-hover:text-orange-500">
						add_a_photo
					</span>
				)}
			</div>

			{/* Text and Info Container */}
			<div className="flex-1 space-y-2">
				<h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">
					{t("mediaBatchInit.fineTuning.retainedSampleReference")}
				</h4>
				<p className="text-xs text-slate-500 leading-relaxed">
		{t("mediaBatchInit.fineTuning.retainedSampleRefsubText")}
				</p>

				{/* Error State UI */}
				{uploadError && (
					<div className="flex items-center gap-2 mt-2">
						<span className="material-symbols-outlined text-red-500 text-[14px]">
							error
						</span>
						<p className="text-[11px] font-bold text-red-500">
							{uploadError}
						</p>
					</div>
				)}

				<div className="flex gap-4 pt-2">
					<span className="text-[9px] font-black text-orange-500 uppercase tracking-widest">
						{t("mediaBatchInit.fineTuning.sop")}
					</span>
				</div>
			</div>
		</div>
	);
};

export default SampleReference;
